/* ═══════════════════════════════════════════════════════
   Monmouth Cards — Pokemon Singles Collection Filters
   (Set / Character / Card Number)
   assets/monmouth-pokemon-filters.js

   Set + Character: search + autocomplete + removable chips, multi-select
   — same pattern as Team (Sports) / Set (Sealed Boxes). Picking a
   suggestion adds it to window.MC.multiSelect's 'set' / 'character'
   category so "Charizard or Pikachu" works the same way "PSA or SGC"
   does elsewhere.

   Card Number: search + autocomplete, single pick — the Filter
   Architecture doc specifies "Search" only (not "+ multi") for this
   field, so picking one just toggles that single tag via
   multiSelect.toggle(tag, null) (the same path Condition/Year use),
   which correctly preserves any active multi-select OR-groups instead
   of the older bespoke "single toggle" pattern this theme used to use.

   All three values (data.sets / data.characters / data.cardNumbers) are
   stored *unprefixed* in the JSON — the set-/player-/cardnum- prefix is
   added here before ever touching multiSelect, matching how the
   checkbox-rendered Pokemon filters (Rarity/Finish/Language/Attributes)
   already carry their prefix baked into the rendered tag.

   Backed by assets/monmouth-pokemon-filters.json (real distinct values
   from retag_pokemon_log.jsonl).

   Re-entrant: window.MC.initPokemonFilters() is called both on first
   page load and after every AJAX section-replace (monmouth-collection.js),
   since that swap destroys and recreates this markup from scratch.
   ═══════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var SET_PREFIX = 'set-';
  var CHARACTER_PREFIX = 'player-';
  var CARDNUM_PREFIX = 'cardnum-';

  var filterDataPromise = null;

  function loadFilterData() {
    if (filterDataPromise) return filterDataPromise;
    var url = window.MC_POKEMON_FILTER_DATA_URL;
    if (!url) return Promise.resolve({ sets: [], characters: [], cardNumbers: [] });
    filterDataPromise = fetch(url)
      .then(function (r) { return r.json(); })
      .catch(function (err) {
        console.warn('[MC] Failed to load Pokemon filter data:', err);
        return { sets: [], characters: [], cardNumbers: [] };
      });
    return filterDataPromise;
  }

  function handleize(str) {
    if (window.MC && typeof window.MC.handleize === 'function') return window.MC.handleize(str);
    return String(str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function escapeHtml(s) {
    var div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  /* ══════════════════════════════════════════════
     Shared "click outside closes the dropdown" listener.
  ══════════════════════════════════════════════ */
  var dismissPairs = [
    { inputId: 'mc-pkm-set-search', boxId: 'mc-pkm-set-suggestions' },
    { inputId: 'mc-pkm-character-search', boxId: 'mc-pkm-character-suggestions' },
    { inputId: 'mc-pkm-cardnum-search', boxId: 'mc-pkm-cardnum-suggestions' }
  ];

  function installGlobalDismissListener() {
    if (window._mcPokemonDismissInstalled) return;
    window._mcPokemonDismissInstalled = true;
    document.addEventListener('click', function (e) {
      dismissPairs.forEach(function (pair) {
        var box = document.getElementById(pair.boxId);
        var input = document.getElementById(pair.inputId);
        if (box && input && !box.contains(e.target) && e.target !== input) {
          box.hidden = true;
        }
      });
    });
  }

  /* Generic autocomplete-dropdown wiring shared by all three widgets. */
  function wireAutocomplete(opts) {
    var input = document.getElementById(opts.inputId);
    var box = document.getElementById(opts.boxId);
    if (!input || !box) return;

    var currentMatches = [];
    var activeIndex = -1;

    function renderSuggestions(matches, query) {
      currentMatches = matches;
      activeIndex = -1;
      if (!matches.length) {
        box.innerHTML = query
          ? '<div class="mc-sidebar__suggestion mc-sidebar__suggestion--empty">' + escapeHtml(opts.emptyLabel) + '</div>'
          : '';
        box.hidden = !query;
        return;
      }
      box.innerHTML = matches.map(function (name, i) {
        return '<button type="button" class="mc-sidebar__suggestion" data-index="' + i + '">' + escapeHtml(name) + '</button>';
      }).join('');
      box.hidden = false;
    }

    function highlight(items) {
      items.forEach(function (el, i) {
        el.classList.toggle('mc-sidebar__suggestion--active', i === activeIndex);
      });
    }

    function choose(name) {
      if (!name) return;
      box.hidden = true;
      opts.onChoose(name, input);
    }

    input.addEventListener('input', function () {
      var query = input.value.trim().toLowerCase();
      if (!query) { renderSuggestions([], ''); return; }
      loadFilterData().then(function (data) {
        var pool = opts.getPool(data);
        var matches = pool
          .filter(function (name) { return name.toLowerCase().indexOf(query) !== -1; })
          .slice(0, 8);
        renderSuggestions(matches, query);
      });
    });

    box.addEventListener('click', function (e) {
      var btn = e.target.closest('.mc-sidebar__suggestion');
      if (!btn || btn.classList.contains('mc-sidebar__suggestion--empty')) return;
      choose(currentMatches[parseInt(btn.dataset.index, 10)]);
    });

    input.addEventListener('keydown', function (e) {
      if (box.hidden || !currentMatches.length) return;
      var items = box.querySelectorAll('.mc-sidebar__suggestion:not(.mc-sidebar__suggestion--empty)');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        highlight(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
        highlight(items);
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0) {
          e.preventDefault();
          choose(currentMatches[activeIndex]);
        }
      } else if (e.key === 'Escape') {
        box.hidden = true;
      }
    });
  }

  /* ══════════════════════════════════════════════
     Generic multi-select chip renderer, shared by Set and Character.
  ══════════════════════════════════════════════ */
  function renderChips(containerId, category) {
    var container = document.getElementById(containerId);
    if (!container) return;
    var state = window.MC && window.MC.multiSelect ? window.MC.multiSelect.getState() : null;
    var selected = (state && state.multi && state.multi[category]) || [];
    var prefix = category === 'set' ? SET_PREFIX : CHARACTER_PREFIX;

    container.innerHTML = selected.map(function (tag) {
      var label = tag.slice(0, prefix.length) === prefix ? tag.slice(prefix.length) : tag;
      return '<button type="button" class="mc-sidebar__chip" data-chip="1">' +
        escapeHtml(label) +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>';
    }).join('');

    Array.from(container.children).forEach(function (chip, i) {
      chip.addEventListener('click', function () {
        if (window.MC && window.MC.multiSelect) window.MC.multiSelect.toggle(selected[i], category);
      });
    });
  }

  /* ══════════════════════════════════════════════
     SET — search + autocomplete + multi-select chips
  ══════════════════════════════════════════════ */
  function initSetSearch() {
    var input = document.getElementById('mc-pkm-set-search');
    if (input) {
      wireAutocomplete({
        inputId: 'mc-pkm-set-search',
        boxId: 'mc-pkm-set-suggestions',
        emptyLabel: 'No sets found',
        getPool: function (data) {
          var state = window.MC && window.MC.multiSelect ? window.MC.multiSelect.getState() : null;
          var selected = (state && state.multi && state.multi.set) || [];
          var selectedHandles = selected.map(handleize);
          return (data.sets || []).filter(function (s) { return selectedHandles.indexOf(handleize(SET_PREFIX + s)) === -1; });
        },
        onChoose: function (name, inputEl) {
          inputEl.value = '';
          if (window.MC && window.MC.multiSelect) window.MC.multiSelect.toggle(SET_PREFIX + name, 'set');
        }
      });
    }
    renderChips('mc-pkm-set-chips', 'set');
  }

  /* ══════════════════════════════════════════════
     CHARACTER — search + autocomplete + multi-select chips
  ══════════════════════════════════════════════ */
  function initCharacterSearch() {
    var input = document.getElementById('mc-pkm-character-search');
    if (input) {
      wireAutocomplete({
        inputId: 'mc-pkm-character-search',
        boxId: 'mc-pkm-character-suggestions',
        emptyLabel: 'No characters found',
        getPool: function (data) {
          var state = window.MC && window.MC.multiSelect ? window.MC.multiSelect.getState() : null;
          var selected = (state && state.multi && state.multi.character) || [];
          var selectedHandles = selected.map(handleize);
          return (data.characters || []).filter(function (c) { return selectedHandles.indexOf(handleize(CHARACTER_PREFIX + c)) === -1; });
        },
        onChoose: function (name, inputEl) {
          inputEl.value = '';
          if (window.MC && window.MC.multiSelect) window.MC.multiSelect.toggle(CHARACTER_PREFIX + name, 'character');
        }
      });
    }
    renderChips('mc-pkm-character-chips', 'character');
  }

  /* ══════════════════════════════════════════════
     CARD NUMBER — search + autocomplete, single pick (no chips)
  ══════════════════════════════════════════════ */
  function initCardNumberSearch() {
    var input = document.getElementById('mc-pkm-cardnum-search');
    if (!input) return;
    wireAutocomplete({
      inputId: 'mc-pkm-cardnum-search',
      boxId: 'mc-pkm-cardnum-suggestions',
      emptyLabel: 'No card numbers found',
      getPool: function (data) { return data.cardNumbers || []; },
      onChoose: function (name, inputEl) {
        inputEl.value = name;
        /* category: null -> a single toggled tag (like Condition/Year),
           not a multi-select axis — correctly preserves any active
           multi-select OR-groups instead of overwriting them. */
        if (window.MC && window.MC.multiSelect) window.MC.multiSelect.toggle(CARDNUM_PREFIX + name, null);
      }
    });
  }

  function init() {
    initSetSearch();
    initCharacterSearch();
    initCardNumberSearch();
    installGlobalDismissListener();
  }

  window.MC = window.MC || {};
  window.MC.initPokemonFilters = init;
  window.MC.renderPokemonChips = function () {
    renderChips('mc-pkm-set-chips', 'set');
    renderChips('mc-pkm-character-chips', 'character');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
